import React, { useState } from 'react';
import { GripVertical, RotateCcw } from 'lucide-react';

/**
 * Componente para reordenar slides mediante drag & drop
 * Usa HTML5 drag events (sin dependencias adicionales)
 */
const SlideReorderer = ({ slides, onReorder, isOpen, onClose }) => {
  const [draggedSlide, setDraggedSlide] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [reorderedSlides, setReorderedSlides] = useState([...slides]);

  const handleDragStart = (e, idx) => {
    setDraggedSlide(idx);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setHoveredIndex(idx);
  };

  const handleDragLeave = () => {
    setHoveredIndex(null);
  };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (draggedSlide === null || draggedSlide === dropIdx) {
      setHoveredIndex(null);
      return;
    }

    const newSlides = [...reorderedSlides];
    const draggedItem = newSlides[draggedSlide];
    newSlides.splice(draggedSlide, 1);
    newSlides.splice(dropIdx, 0, draggedItem);

    setReorderedSlides(newSlides);
    setDraggedSlide(null);
    setHoveredIndex(null);
  };

  const handleReset = () => {
    setReorderedSlides([...slides]);
    setDraggedSlide(null);
    setHoveredIndex(null);
  };

  const handleApply = () => {
    onReorder(reorderedSlides);
    onClose();
  };

  const isChanged = JSON.stringify(slides) !== JSON.stringify(reorderedSlides);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 modal-safe-area bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Reordenar Slides</h2>
            <p className="text-sm text-gray-600 mt-1">
              Arrastra y suelta para cambiar el orden de las slides
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Lista de slides - Draggable */}
        <div className="flex-1 overflow-y-auto space-y-2 mb-4">
          {reorderedSlides.map((slide, idx) => (
            <div
              key={slide.id || idx}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, idx)}
              className={`p-4 rounded-lg border-2 transition-all cursor-move ${
                draggedSlide === idx
                  ? 'opacity-50 border-red-500 bg-red-50'
                  : hoveredIndex === idx
                  ? 'border-blue-500 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Drag Handle */}
                <div className="flex-shrink-0 text-gray-400 mt-1">
                  <GripVertical size={20} />
                </div>

                {/* Contenido */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">
                      Slide {idx + 1}
                    </span>
                    {draggedSlide === idx && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                        Arrastrando
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-gray-600 mt-1">
                    {slide.type === 'cover' && '📌 Portada'}
                    {slide.type === 'storytelling' && '📖 Storytelling'}
                    {slide.type === 'storytelling_results' && '📊 Resultados'}
                    {slide.type === 'campaigns' && '🎯 Campañas'}
                    {slide.type === 'closing' && '👋 Cierre'}
                    {!['cover', 'storytelling', 'storytelling_results', 'campaigns', 'closing'].includes(slide.type) && slide.type}
                  </p>

                  {/* Preview del contenido */}
                  <div className="mt-1 text-xs text-gray-600 line-clamp-1">
                    {slide.editable_elements?.[0]?.content ||
                      slide.editable_elements?.[0]?.title ||
                      'Sin contenido'}
                  </div>
                </div>

                {/* Número visual */}
                <div className="flex-shrink-0 text-gray-400 font-semibold text-lg">
                  {idx + 1}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Información */}
        {isChanged && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            ✓ {reorderedSlides.length} slides listos para reordenar
          </div>
        )}

        {/* Footer - Botones de acción */}
        <div className="flex gap-3 pt-4 border-t">
          <button
            onClick={handleReset}
            disabled={!isChanged}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
          >
            <RotateCcw size={16} />
            Resetear
          </button>

          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            Cancelar
          </button>

          <button
            onClick={handleApply}
            disabled={!isChanged}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
          >
            Aplicar Cambios
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlideReorderer;
